import React from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { classes, st } from './BookingDetails.st.css';
import { useSettings } from '@wix/tpa-settings/react';
import settingsParams from '../../settingsParams';
import { getContent } from '../../../../utils/content/content';
import { ServiceData } from '../../../../utils/state/types';
import { FormSelectedSlot } from '../../../../types/formSelectedSlots';
import { getServiceSlotIdentifier } from '../../../../utils';
import SingleSlotDetails from './SingleSlotDetails/SingleSlotDetails';
import { Accordion, Divider } from 'wix-ui-tpa/cssVars';
import { BookingDetailsDataHook } from './dataHooks';
import Text, { TextType } from '../../Text/Text';

export interface BookingDetailsProps {
  serviceData: ServiceData;
  formSelectedSlot: FormSelectedSlot;
  numberOfParticipants?: number;
}

const SERVICE_DETAILS_TITLE = 'service-details';

const BookingDetails: React.FC<BookingDetailsProps> = ({
  serviceData,
  formSelectedSlot,
  numberOfParticipants,
}) => {
  const { isMobile } = useEnvironment();
  const settings = useSettings();

  const { isSingleService, slotServices } = serviceData;

  const sectionTitle = getContent({
    settings,
    settingsParam: settingsParams.summarySectionTitle,
  });

  const tagName = settings.get(settingsParams.summarySectionTagName);

  const renderMobile = () => (
    <Accordion
      className={classes.accordion}
      data-hook={BookingDetailsDataHook.ACCORDION}
    >
      <Accordion.Item
        id={SERVICE_DETAILS_TITLE}
        title={sectionTitle}
        titleTag={tagName}
      >
        {renderContent()}
      </Accordion.Item>
    </Accordion>
  );

  const renderDesktop = () => (
    <>
      <Text
        tagName={tagName}
        type={TextType.Title}
        data-hook={BookingDetailsDataHook.SECTION_TITLE}
      >
        {sectionTitle}
      </Text>
      <Divider className={st(classes.divider, classes.divider1)} />
      {renderContent()}
    </>
  );

  const renderContent = () =>
    formSelectedSlot.nestedSlots.map((slot, index, slots) => {
      const slotId = getServiceSlotIdentifier(slot);
      const slotService = slotServices[slotId];
      const isLastSlot = index === slots.length - 1;
      return (
        <React.Fragment key={slotId}>
          <div className={index < slots.length ? classes.item : ''}>
            <SingleSlotDetails
              isSingleService={isSingleService}
              slot={slot}
              slotService={slotService}
              totalNumberOfParticipants={numberOfParticipants}
            />
          </div>
          {!isSingleService && !isLastSlot && (
            <Divider
              className={st(classes.divider, classes.multiServiceDivider)}
            />
          )}
        </React.Fragment>
      );
    });

  return (
    <div
      className={st(classes.root, {
        isMobile,
        isMultiServices: !isSingleService,
      })}
    >
      {isMobile ? renderMobile() : renderDesktop()}
    </div>
  );
};

export default BookingDetails;
